/**
 * A tab open across a deploy holds route chunks (`lazy()` in App.tsx) whose hashed filenames
 * the server no longer serves — the dynamic import 404s, Caddy's SPA fallback returns
 * index.html for it, and the browser rejects that as invalid JS. Vite's own import wrapper
 * catches this and fires `vite:preloadError` on window before the error reaches React; left
 * alone it then rethrows into AppErrorBoundary, whose "Try again" / "Go to home" both still run
 * the same stale bundle and so hit the same failure again. A reload fetches the current
 * index.html and current chunks, which actually clears it.
 *
 * Once per build: a second failure under the same build is not a stale bundle, so it falls
 * through to the error boundary rather than looping, and the next deploy can still recover.
 */
const RELOAD_GUARD_KEY = 'hapmetrix.chunk-reload-attempted';

function currentBuild(): string {
  return document.querySelector('meta[name="app-build"]')?.getAttribute('content') ?? '';
}

export function installChunkReloadRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    try {
      const build = currentBuild();
      if (window.sessionStorage.getItem(RELOAD_GUARD_KEY) === build) return;
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, build);
    } catch {
      // Storage refusal costs the loop guard only — still reload once, worst case a loop
      // in a browser mode that already disables persistent storage.
    }
    event.preventDefault();
    window.location.reload();
  });
}
